/**
 * Modul "Benzin-Route" (vormals "Unsere Route" - siehe Migration 0053; die
 * Umbenennung ist rein kosmetisch im Frontend, Tabellen-/Berechtigungs-
 * schlüssel bleiben bewusst "route"/"route_*"): drei unabhängige Listen -
 * "Eingekaufte Familien", "POH-Arbeiter" und "LFC-Arbeiter". POH- und
 * LFC-Arbeiter teilen sich dieselbe Tabelle (route_workers, Spalte
 * worker_type) und dieselbe Formularstruktur (WorkersSection/WorkerForm
 * unten sind für beide parametrisiert), sind aber über worker_type strikt
 * getrennt gespeichert/gefiltert - eine Vermischung ist dadurch
 * ausgeschlossen, und worker_type ist nach dem Anlegen nicht mehr änderbar.
 *
 * Rechte-Modell (siehe Migration 0045/0053, VIER Berechtigungen, Kategorie
 * "Benzin-Route"):
 *   - route.view               Grundvoraussetzung (Ansicht, Menüpunkt, siehe
 *                               App.tsx/Sidebar.tsx/MobileDrawer.tsx) sowie
 *                               POH-/LFC-Arbeiter hinzufügen/bearbeiten -
 *                               JEDE reguläre Rolle.
 *   - route.families.manage    Familien hinzufügen/bearbeiten/löschen.
 *   - route.workers.delete     NUR POH-Arbeiter löschen.
 *   - route.sd_workers.delete  NUR LFC-Arbeiter löschen (z.B. "Routenpartner":
 *                               darf LFC-Arbeiter löschen, POH-Arbeiter nicht).
 * Die Datei heißt bewusst RoutePage (statt "Route"), um keine
 * Namenskollision mit dem gleichnamigen <Route>-Element aus react-router-dom
 * in App.tsx zu erzeugen.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card, CardHeader } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import type { RouteFamily, RouteMemberOption, RouteWorker } from "../../types";

const STATUS_LABELS: Record<RouteWorker["payment_status"], string> = {
  open: "Offen", paid: "Bezahlt", unpaid: "Nicht bezahlt",
};
const STATUS_TONE: Record<RouteWorker["payment_status"], "success" | "danger" | "warning"> = {
  open: "warning", paid: "success", unpaid: "danger",
};

export default function RoutePage() {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-100">Benzin-Route</h1>
        <p className="text-sm text-gray-500 mt-1">Verwaltung eingekaufter Familien, POH-Arbeiter und LFC-Arbeiter.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <FamiliesSection />
        <WorkersSection workerType="poh" title="POH-Arbeiter" deletePermission="route.workers.delete" />
        <WorkersSection workerType="sd" title="LFC-Arbeiter" deletePermission="route.sd_workers.delete" />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Eingekaufte Familien
// ----------------------------------------------------------------------------
function FamiliesSection() {
  const canManage = usePermission("route.families.manage");
  const [families, setFamilies] = useState<RouteFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<RouteFamily | null | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setFamilies(await api.get<RouteFamily[]>("/api/route/families"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(f: RouteFamily) {
    if (!confirm("Möchtest du diese Daten wirklich löschen?")) return;
    try {
      await api.delete(`/api/route/families/${f.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <Card>
      <CardHeader
        title="Eingekaufte Familien"
        action={canManage && <button className="btn-primary" onClick={() => setEditing(null)}>+ Hinzufügen</button>}
      />
      {loading ? <Spinner /> : families.length === 0 ? (
        <EmptyState message="Keine Familien erfasst." />
      ) : (
        <div className="space-y-3">
          {families.map((f) => (
            <div key={f.id} className="bg-surface-raised rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-100 truncate">{f.family_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Eingekauft bis: {f.bought_until || "—"}</p>
                  {f.notes && <p className="text-sm text-gray-400 mt-1.5 whitespace-pre-wrap">{f.notes}</p>}
                </div>
                {canManage && (
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <button className="text-accent text-xs hover:underline" onClick={() => setEditing(f)}>Bearbeiten</button>
                    <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(f)}>Löschen</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== undefined && (
        <FamilyForm family={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </Card>
  );
}

function FamilyForm({ family, onClose, onSaved }: { family: RouteFamily | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!family;
  const [familyName, setFamilyName] = useState(family?.family_name ?? "");
  const [boughtUntil, setBoughtUntil] = useState(family?.bought_until ?? "");
  const [notes, setNotes] = useState(family?.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (isEdit) await api.put(`/api/route/families/${family!.id}`, { familyName, boughtUntil, notes });
      else await api.post("/api/route/families", { familyName, boughtUntil, notes });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? "Familie bearbeiten" : "Neue Familie"}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="route-family-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="route-family-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Familienname *</label>
          <input className="input" value={familyName} onChange={(e) => setFamilyName(e.target.value)} required autoFocus />
        </div>
        <div>
          <label className="label">Eingekauft bis</label>
          <input className="input" value={boughtUntil} onChange={(e) => setBoughtUntil(e.target.value)} />
        </div>
        <div>
          <label className="label">Notizen</label>
          <textarea className="input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// POH-Arbeiter / LFC-Arbeiter (dieselbe Tabelle, per worker_type getrennt -
// siehe Docstring oben)
// ----------------------------------------------------------------------------
function WorkersSection({ workerType, title, deletePermission }: {
  workerType: RouteWorker["worker_type"]; title: string; deletePermission: string;
}) {
  const canDelete = usePermission(deletePermission);
  const [workers, setWorkers] = useState<RouteWorker[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<RouteWorker | null | undefined>(undefined);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await api.get<RouteWorker[]>("/api/route/workers");
      setWorkers(all.filter((w) => w.worker_type === workerType));
    } finally {
      setLoading(false);
    }
  }, [workerType]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(w: RouteWorker) {
    if (!confirm("Möchtest du diese Daten wirklich löschen?")) return;
    try {
      await api.delete(`/api/route/workers/${w.id}`);
      load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <Card>
      <CardHeader
        title={title}
        action={<button className="btn-primary" onClick={() => setEditing(null)}>+ Hinzufügen</button>}
      />
      {loading ? <Spinner /> : workers.length === 0 ? (
        <EmptyState message={`Keine ${title} erfasst.`} />
      ) : (
        <div className="space-y-3">
          {workers.map((w) => (
            <div key={w.id} className="bg-surface-raised rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-100 truncate">{w.worker_name}</p>
                    <Badge tone={STATUS_TONE[w.payment_status]}>{STATUS_LABELS[w.payment_status]}</Badge>
                  </div>
                  {w.member_id && (
                    <p className="text-xs text-gray-500 mt-0.5">Mitarbeiter von: {w.member_last_name}, {w.member_first_name}</p>
                  )}
                  {w.notes && <p className="text-sm text-gray-400 mt-1.5 whitespace-pre-wrap">{w.notes}</p>}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <button className="text-accent text-xs hover:underline" onClick={() => setEditing(w)}>Bearbeiten</button>
                  {canDelete && (
                    <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(w)}>Löschen</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== undefined && (
        <WorkerForm
          worker={editing}
          workerType={workerType}
          title={title}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}
    </Card>
  );
}

function WorkerForm({ worker, workerType, title, onClose, onSaved }: {
  worker: RouteWorker | null; workerType: RouteWorker["worker_type"]; title: string; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!worker;
  const [workerName, setWorkerName] = useState(worker?.worker_name ?? "");
  const [notes, setNotes] = useState(worker?.notes ?? "");
  const [paymentStatus, setPaymentStatus] = useState<RouteWorker["payment_status"]>(worker?.payment_status ?? "open");
  const [memberId, setMemberId] = useState(worker?.member_id ? String(worker.member_id) : "");
  const [members, setMembers] = useState<RouteMemberOption[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get<RouteMemberOption[]>("/api/route/members-list")
      .then(setMembers)
      .finally(() => setMembersLoading(false));
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = { workerName, notes, paymentStatus, memberId: memberId ? Number(memberId) : null };
      // "workerType" wird bewusst nur beim Anlegen mitgeschickt - siehe
      // Docstring oben (nach dem Anlegen nicht mehr änderbar, das Backend
      // ignoriert das Feld bei PUT ohnehin).
      if (isEdit) await api.put(`/api/route/workers/${worker!.id}`, payload);
      else await api.post("/api/route/workers", { ...payload, workerType });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? `${title} bearbeiten` : `Neuer ${title}`}
      onClose={onClose}
      footer={<>
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
        <button className="btn-primary" form="route-worker-form" disabled={submitting}>{submitting ? "Speichern…" : "Speichern"}</button>
      </>}
    >
      <form id="route-worker-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <div>
          <label className="label">Name des Arbeiters *</label>
          <input className="input" value={workerName} onChange={(e) => setWorkerName(e.target.value)} required autoFocus />
        </div>
        <div>
          <label className="label">Mitarbeiter von</label>
          <select className="input" value={memberId} onChange={(e) => setMemberId(e.target.value)} disabled={membersLoading}>
            <option value="">— keiner —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.last_name}, {m.first_name}{m.rank ? ` — ${m.rank}` : ""}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">Wird immer live aus der Mitgliederliste angezeigt - eine Umbenennung dort wirkt sich automatisch hier aus.</p>
        </div>
        <div>
          <label className="label">Notizen</label>
          <textarea className="input" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div>
          <label className="label">Abgabe bezahlt?</label>
          <select className="input" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value as RouteWorker["payment_status"])}>
            <option value="open">Offen</option>
            <option value="paid">Bezahlt</option>
            <option value="unpaid">Nicht bezahlt</option>
          </select>
        </div>
      </form>
    </Modal>
  );
}
